import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { ToasterService } from '@lib/components/toaster/toaster.service';
import { selectAppSession } from '@lib/store';
import { isValidSession } from '@lib/utils/auth/auth.utils';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';
import { AuthService } from '../auth/auth.service';

@Injectable({
    providedIn: 'root',
})
export class SessionMonitorService {
    private readonly visibilityHandler: () => void;
    private readonly focusHandler: () => void;
    private isValidating = false;
    private validationTimeout?: number;

    constructor(
        private readonly store: Store,
        private readonly authService: AuthService,
        private readonly ngZone: NgZone,
        private readonly router: Router,
        private readonly toaster: ToasterService,
        @Inject(DOCUMENT) private readonly document: Document,
    ) {
        this.visibilityHandler = this.handleVisibilityChange.bind(this);
        this.focusHandler = this.handleWindowFocus.bind(this);

        this.init();
    }

    private init(): void {
        this.document.addEventListener('visibilitychange', this.visibilityHandler);

        globalThis.addEventListener('focus', this.focusHandler);

        globalThis.addEventListener('beforeunload', this.cleanup.bind(this));
    }

    private handleVisibilityChange(): void {
        if (this.document.visibilityState === 'visible') {
            this.debouncedValidation();
        }
    }

    private handleWindowFocus(): void {
        this.debouncedValidation();
    }

    private debouncedValidation(): void {
        if (this.validationTimeout) {
            clearTimeout(this.validationTimeout);
        }

        this.validationTimeout = globalThis.setTimeout(() => {
            this.ngZone.run(() => {
                this.validateSessionOnTabFocus();
            });
        }, 300) as any;
    }

    private validateSessionOnTabFocus(): void {
        if (this.isValidating) {
            return;
        }

        this.isValidating = true;

        try {
            this.store
                .select(selectAppSession)
                .pipe(take(1))
                .subscribe({
                    next: (session) => {
                        if (session && !isValidSession(session)) {
                            this.performLogout();
                        }
                    },
                    error: (error) => {
                        console.warn('Session validation error:', error);
                    },
                    complete: () => {
                        this.isValidating = false;
                    },
                });
        } catch (error) {
            console.warn('Failed to validate session on tab focus:', error);
            this.isValidating = false;
        }
    }

    private performLogout(): void {
        try {
            this.authService.logout();
            this.router.navigate(['/auth/login'], {
                queryParams: { reason: 'session_expired' },
            });
        } catch (error) {
            this.toaster.show({
                message: 'Session expired. Redirecting to login...',
                status: 'warning',
            });
            console.warn('Failed to perform logout:', error);
            this.document.defaultView!.location.href = '/auth/login?reason=session_expired';
        }
    }

    private cleanup(): void {
        try {
            if (this.validationTimeout) {
                clearTimeout(this.validationTimeout);
            }

            this.document.removeEventListener('visibilitychange', this.visibilityHandler);
            globalThis.removeEventListener('focus', this.focusHandler);
        } catch (error) {
            console.warn('Failed to cleanup session monitor:', error);
        }
    }

    public validateSession(): boolean {
        try {
            return this.authService.validateCurrentSession();
        } catch (error) {
            console.warn('Manual session validation failed:', error);
            return false;
        }
    }

    public isMonitoring(): boolean {
        return !this.isValidating;
    }
}
